import { useState } from 'react';
import axios from 'axios';
import { DIRECTUS_INSTANCE, directusAPI, getWorkerApiUrl } from '../../../api';
import { buildUploadedThumbnailList } from '../lib/articlePayload';

/**
 * Thumbnail (featured image) state of the article editor, including gallery / delete /
 * set-as-main modals. `applyThumbnailState` applies the result of resolveThumbnailState().
 */
export function useArticleThumbnailState() {
  const [thumbnailData, setThumbnailData] = useState(null);
  const [thumbnailArray, setThumbnailArray] = useState([]);
  const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0);
  const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isThumbnailGenerating, setIsThumbnailGenerating] = useState(false);
  const [thumbnailGenerationStartTime, setThumbnailGenerationStartTime] = useState(null);

  // Thumbnail modal states
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSetMainModal, setShowSetMainModal] = useState(false);
  const [thumbnailToDelete, setThumbnailToDelete] = useState(null);
  const [thumbnailToSetMain, setThumbnailToSetMain] = useState(null);
  const [isProcessingThumbnail, setIsProcessingThumbnail] = useState(false);

  const applyThumbnailState = (state) => {
    if ('thumbnailData' in state) setThumbnailData(state.thumbnailData);
    if ('thumbnailArray' in state) setThumbnailArray(state.thumbnailArray);
    if ('currentThumbnailIndex' in state) setCurrentThumbnailIndex(state.currentThumbnailIndex);
    if ('isThumbnailGenerating' in state) setIsThumbnailGenerating(state.isThumbnailGenerating);
    if ('thumbnailGenerationStartTime' in state) setThumbnailGenerationStartTime(state.thumbnailGenerationStartTime);
  };

  return {
    thumbnailData,
    setThumbnailData,
    thumbnailArray,
    setThumbnailArray,
    currentThumbnailIndex,
    setCurrentThumbnailIndex,
    isDraggingThumbnail,
    setIsDraggingThumbnail,
    isUploadingThumbnail,
    setIsUploadingThumbnail,
    isThumbnailGenerating,
    setIsThumbnailGenerating,
    thumbnailGenerationStartTime,
    setThumbnailGenerationStartTime,
    showGalleryModal,
    setShowGalleryModal,
    showDeleteModal,
    setShowDeleteModal,
    showSetMainModal,
    setShowSetMainModal,
    thumbnailToDelete,
    setThumbnailToDelete,
    thumbnailToSetMain,
    setThumbnailToSetMain,
    isProcessingThumbnail,
    setIsProcessingThumbnail,
    applyThumbnailState,
  };
}

/**
 * Thumbnail actions: upload (drop / file picker), carousel navigation, set main,
 * regenerate via worker API, delete and download.
 */
export function useArticleThumbnailActions({ id, token, fetchArticle, thumbnails }) {
  const {
    thumbnailData,
    setThumbnailData,
    thumbnailArray,
    setThumbnailArray,
    currentThumbnailIndex,
    setCurrentThumbnailIndex,
    setIsDraggingThumbnail,
    setIsUploadingThumbnail,
    setShowDeleteModal,
    setShowSetMainModal,
    thumbnailToDelete,
    setThumbnailToDelete,
    setThumbnailToSetMain,
    setIsProcessingThumbnail,
  } = thumbnails;

  const handleThumbnailUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      window.alert('Please upload a valid image file');
      return;
    }

    setIsUploadingThumbnail(true);
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload to Directus
      const uploadResponse = await directusAPI.post('/files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const fileId = uploadResponse.data.data.id;
      const fileUrl = `${DIRECTUS_INSTANCE}/assets/${fileId}`;
      const updatedThumbnails = buildUploadedThumbnailList(thumbnailArray, {
        fileId,
        fileUrl,
        createdAt: new Date().toISOString(),
      });

      // Update article with thumbnail array (new format)
      try {
        await directusAPI.patch(`/items/articles/${id}`, {
          article_thumbnail_url: updatedThumbnails,
          article_thumbnail_included: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (patchError) {
        // Fallback for environments that still expect legacy string format.
        console.warn('⚠️ Thumbnail array save failed, trying legacy string format', patchError);
        await directusAPI.patch(`/items/articles/${id}`, {
          article_thumbnail_url: fileUrl,
          article_thumbnail_included: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Update local state
      setThumbnailArray(updatedThumbnails);
      setCurrentThumbnailIndex(updatedThumbnails.length - 1);
      setThumbnailData({ url: `${fileUrl}?t=${Date.now()}` });
      console.log('✅ Thumbnail uploaded successfully:', fileUrl);
    } catch (error) {
      console.error('❌ Error uploading thumbnail:', error);
      window.alert('Failed to upload thumbnail. Please try again.');
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  const handleThumbnailDrop = (e) => {
    e.preventDefault();
    setIsDraggingThumbnail(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleThumbnailUpload(file);
    }
  };

  const handleThumbnailDragOver = (e) => {
    e.preventDefault();
    setIsDraggingThumbnail(true);
  };

  const handleThumbnailDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingThumbnail(false);
  };

  const handleThumbnailFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleThumbnailUpload(file);
    }
  };

  // Thumbnail carousel navigation functions
  const handleThumbnailSelect = (index) => {
    if (index >= 0 && index < thumbnailArray.length) {
      setCurrentThumbnailIndex(index);
      setThumbnailData({ url: thumbnailArray[index].url });
    }
  };

  const handleThumbnailPrevious = () => {
    const prevIndex = currentThumbnailIndex > 0 ? currentThumbnailIndex - 1 : thumbnailArray.length - 1;
    handleThumbnailSelect(prevIndex);
  };

  const handleThumbnailNext = () => {
    const nextIndex = currentThumbnailIndex < thumbnailArray.length - 1 ? currentThumbnailIndex + 1 : 0;
    handleThumbnailSelect(nextIndex);
  };

  const handleSetAsThumbnail = async () => {
    if (thumbnailArray.length <= 1) return; // No need to set if only one thumbnail

    // Show confirmation modal
    setThumbnailToSetMain(thumbnailArray[currentThumbnailIndex]);
    setShowSetMainModal(true);
  };

  const confirmSetAsThumbnail = async () => {
    setIsProcessingThumbnail(true);
    try {
      // Update the thumbnail array to mark current as chosen
      const updatedThumbnails = thumbnailArray.map((thumb, index) => ({
        ...thumb,
        chosen: index === currentThumbnailIndex
      }));

      // Save to Directus
      await directusAPI.patch(`/items/articles/${id}`, {
        article_thumbnail_url: updatedThumbnails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setThumbnailArray(updatedThumbnails);

      console.log('✅ Thumbnail set as main:', thumbnailArray[currentThumbnailIndex].url);

      // Close modal
      setShowSetMainModal(false);
      setThumbnailToSetMain(null);

    } catch (error) {
      console.error('❌ Error setting thumbnail:', error);
      window.alert('Failed to set thumbnail. Please try again.');
    } finally {
      setIsProcessingThumbnail(false);
    }
  };

  const handleThumbnailRegenerate = async () => {
    setIsUploadingThumbnail(true);
    try {
      // Manual regenerate should always be allowed; non-blocking guard update.
      try {
        await directusAPI.patch(`/items/articles/${id}`, {
          article_thumbnail_included: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (enableError) {
        console.warn('⚠️ Could not set article_thumbnail_included before regenerate:', enableError);
      }

      // Call backend API to regenerate thumbnail
      const apiUrl = getWorkerApiUrl('/api/regenerate-thumbnail');

      const response = await axios.post(apiUrl, {
        articleId: id,
        userId: localStorage.getItem('user_id')
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Handle new JSON array format
        if (response.data.thumbnails && Array.isArray(response.data.thumbnails)) {
          setThumbnailArray(response.data.thumbnails);
          const chosenIndex = response.data.thumbnails.findIndex(thumb => thumb.chosen);
          const activeIndex = chosenIndex >= 0 ? chosenIndex : 0;
          setCurrentThumbnailIndex(activeIndex);

          // Force reload the image by adding a cache-busting parameter
          const thumbnailUrlWithCacheBust = `${response.data.thumbnails[activeIndex].url}?t=${Date.now()}`;
          setThumbnailData({ url: thumbnailUrlWithCacheBust });

          console.log('✅ Thumbnail regenerated:', response.data.thumbnails[activeIndex].url);
          console.log('📊 Total thumbnails:', response.data.total_thumbnails);
        } else if (response.data.thumbnail_url) {
          // Fallback for old format
          const thumbnailUrlWithCacheBust = `${response.data.thumbnail_url}?t=${Date.now()}`;
          setThumbnailData({ url: thumbnailUrlWithCacheBust });
          console.log('✅ Thumbnail regenerated (old format):', response.data.thumbnail_url);
        }

        // Refetch article data to ensure everything is in sync
        setTimeout(() => {
          fetchArticle();
        }, 500);
      } else {
        throw new Error('No thumbnail URL returned');
      }
    } catch (error) {
      console.error('❌ Error regenerating thumbnail:', error);
      const backendMessage = error.response?.data?.error || error.response?.data?.message;
      window.alert(backendMessage || 'Failed to regenerate thumbnail. Please try again.');
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  const handleThumbnailDelete = async () => {
    if (!thumbnailArray.length) return;

    // Show confirmation modal
    setThumbnailToDelete(thumbnailArray[currentThumbnailIndex]);
    setShowDeleteModal(true);
  };

  const confirmDeleteThumbnail = async () => {
    if (!thumbnailToDelete) return;

    setIsProcessingThumbnail(true);
    try {
      // Remove the current thumbnail from the array
      const updatedThumbnails = thumbnailArray.filter((_, index) => index !== currentThumbnailIndex);

      if (updatedThumbnails.length === 0) {
        // If no thumbnails left, clear everything
        await directusAPI.patch(`/items/articles/${id}`, {
          article_thumbnail_url: null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setThumbnailData(null);
        setThumbnailArray([]);
        setCurrentThumbnailIndex(0);
        console.log('✅ All thumbnails deleted');

        // Close modal
        setShowDeleteModal(false);
        setThumbnailToDelete(null);
        return;
      }

      // If the deleted thumbnail was chosen, choose the first remaining one
      if (thumbnailToDelete.chosen && updatedThumbnails.length > 0) {
        updatedThumbnails[0].chosen = true;
      }

      // Adjust current index if needed
      let newIndex = currentThumbnailIndex;
      if (currentThumbnailIndex >= updatedThumbnails.length) {
        newIndex = updatedThumbnails.length - 1;
      }

      // Save updated array to Directus
      await directusAPI.patch(`/items/articles/${id}`, {
        article_thumbnail_url: updatedThumbnails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setThumbnailArray(updatedThumbnails);
      setCurrentThumbnailIndex(newIndex);
      setThumbnailData({ url: updatedThumbnails[newIndex].url });

      console.log('✅ Thumbnail deleted:', thumbnailToDelete.url);
      console.log('📊 Remaining thumbnails:', updatedThumbnails.length);

      // Close modal
      setShowDeleteModal(false);
      setThumbnailToDelete(null);

    } catch (error) {
      console.error('❌ Error deleting thumbnail:', error);
      window.alert('Failed to delete thumbnail. Please try again.');
    } finally {
      setIsProcessingThumbnail(false);
    }
  };

  const handleThumbnailDownload = async () => {
    if (thumbnailData && thumbnailData.url) {
      try {
        // Fetch the image as a blob
        const response = await fetch(thumbnailData.url);
        const blob = await response.blob();

        // Create a blob URL and download it
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `thumbnail-article-${id}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
        console.log('✅ Thumbnail downloaded');
      } catch (error) {
        console.error('❌ Error downloading thumbnail:', error);
        // Fallback to opening in new tab
        window.open(thumbnailData.url, '_blank');
      }
    }
  };

  return {
    handleThumbnailUpload,
    handleThumbnailDrop,
    handleThumbnailDragOver,
    handleThumbnailDragLeave,
    handleThumbnailFileSelect,
    handleThumbnailSelect,
    handleThumbnailPrevious,
    handleThumbnailNext,
    handleSetAsThumbnail,
    confirmSetAsThumbnail,
    handleThumbnailRegenerate,
    handleThumbnailDelete,
    confirmDeleteThumbnail,
    handleThumbnailDownload,
  };
}
